import { toast } from 'sonner';
import type {
  IMapboxCoordinates,
  IMapboxDirectionsResponse,
  MapboxDirectionsProfile,
} from '@/types/mapbox';
import { getMapboxAccessToken, MAPBOX_API } from '../constants';

/**
 * Options for fetching directions from Mapbox Directions API
 */
interface FetchDirectionsOptions {
  origin: IMapboxCoordinates;
  destination: IMapboxCoordinates;
  profile?: MapboxDirectionsProfile;
  alternatives?: boolean;
  geometries?: 'geojson' | 'polyline' | 'polyline6';
  overview?: 'full' | 'simplified' | 'false';
}

/**
 * Fetch directions from Mapbox Directions API
 *
 * @see https://docs.mapbox.com/api/navigation/directions/
 *
 * @param options - Direction request options
 * @returns Promise resolving to directions response or undefined on error
 *
 * @example
 * ```typescript
 * const directions = await fetchDirections({
 *   origin: { lng: 2.3522, lat: 48.8566 },
 *   destination: { lng: 2.2945, lat: 48.8584 },
 *   profile: 'driving',
 * });
 * ```
 */
export async function fetchDirections({
  origin,
  destination,
  profile = 'driving',
  alternatives = false,
  geometries = 'geojson',
  overview = 'full',
}: FetchDirectionsOptions): Promise<IMapboxDirectionsResponse | undefined> {
  try {
    const token = getMapboxAccessToken();
    const params = new URLSearchParams();
    params.set('access_token', token);
    params.set('alternatives', String(alternatives));
    params.set('geometries', geometries);
    params.set('overview', overview);

    const coordinates = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
    const url = `${MAPBOX_API.DIRECTIONS}/${profile}/${coordinates}?${params}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      const text = await res.text();
      toast.error(`Mapbox directions error: ${text}`);
      return;
    }

    const json: IMapboxDirectionsResponse = await res.json();
    return json;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to fetch directions: ${errorMessage}`);
    return undefined;
  }
}
